import { variant } from "styled-system";
import { WHCM, WHCMstyles } from "../constants";

export const kind = variant({
  prop: "kind",
  variants: {
    primary: {
      bg: "primaryBtnBg",
      borderColor: "primaryBtnBorder",
      color: "primaryBtnText",
      "&:hover": {
        bg: "primaryBtnHoverBg",
        borderColor: "primaryBtnHoverBorder",
        color: "primaryBtnHoverText",
      },
      "&:active": {
        bg: "primaryBtnActiveBg",
        borderColor: "primaryBtnActiveBorder",
        color: "primaryBtnActiveText",
      },
      '&[aria-disabled="true"]': {
        bg: "primaryBtnDisabledBg",
        borderColor: "primaryBtnDisabledBorder",
        color: "primaryBtnDisabledBText",
      },
    },
    secondary: {
      bg: "secondaryBtnBg",
      borderColor: "secondaryBtnBorder",
      color: "secondaryBtnText",
      "&:hover": {
        bg: "secondaryBtnHoverBg",
        borderColor: "secondaryBtnHoverBorder",
        color: "secondaryBtnHoverText",
      },
      "&:active": {
        bg: "secondaryBtnActiveBg",
        borderColor: "secondaryBtnActiveBorder",
        color: "secondaryBtnActiveText",
      },
      '&[aria-disabled="true"]': {
        bg: "secondaryBtnDisabledBg",
        borderColor: "secondaryBtnDisabledBorder",
        color: "secondaryBtnDisabledBText",
      },
    },
    borderless: {
      bg: "borderlessBtnBg",
      borderColor: "borderlessBtnBorder",
      color: "borderlessBtnText",
      px: "btnBorderlessPaddingX",
      cursor: "pointer",
      "&:hover": {
        bg: "borderlessBtnHoverBg",
        borderColor: "borderlessBtnHoverBorder",
        color: "borderlessBtnHoverText",
      },
      "&:active": {
        bg: "borderlessBtnActiveBg",
        borderColor: "borderlessBtnActiveBorder",
        color: "borderlessBtnActiveText",
      },
      '&[aria-disabled="true"]': {
        bg: "borderlessBtnDisabledBg",
        borderColor: "borderlessBtnDisabledBorder",
        color: "borderlessBtnDisabledBText",
      },
    },
    tertiary: {
      bg: "tertiaryBtnBg",
      borderColor: "tertiaryBtnBorder",
      color: "tertiaryBtnText",
      "&:hover": {
        bg: "tertiaryBtnHoverBg",
        borderColor: "tertiaryBtnHoverBorder",
        color: "tertiaryBtnHoverText",
      },
      "&:active": {
        bg: "tertiaryBtnActiveBg",
        borderColor: "tertiaryBtnActiveBorder",
        color: "tertiaryBtnActiveText",
      },
      '&[aria-disabled="true"]': {
        bg: "tertiaryBtnDisabledBg",
        borderColor: "tertiaryBtnDisabledBorder",
        color: "tertiaryBtnDisabledBText",
      },
    },
  },
});

export const vertSize = variant({
  prop: "vertSize",
  variants: {
    32: {
      pt: "btn32PaddingTop",
      px: "btn32PaddingX",
      pb: "btn32PaddingBottom",
      fontSize: "btn32FontSize",
      fontWeight: "btn32FontWeight",
      lineHeight: "btn32LineHeight",
      "& svg": {
        width: "btn32IconSize",
        height: "btn32IconSize",
      },
    },
    40: {
      pt: "btn40PaddingTop",
      px: "btn40PaddingX",
      pb: "btn40PaddingBottom",
      fontSize: "btn40FontSize",
      fontWeight: "btn40FontWeight",
      lineHeight: "btn40LineHeight",
      "& svg": {
        width: "btn40IconSize",
        height: "btn40IconSize",
      },
    },
    48: {
      pt: "btn48PaddingTop",
      px: "btn48PaddingX",
      pb: "btn48PaddingBottom",
      fontSize: "btn48FontSize",
      fontWeight: "btn48FontWeight",
      lineHeight: "btn48LineHeight",
      "& svg": {
        width: "btn48IconSize",
        height: "btn48IconSize",
      },
    },
    56: {
      pt: "btn56PaddingTop",
      px: "btn56PaddingX",
      pb: "btn56PaddingBottom",
      fontSize: "btn56FontSize",
      fontWeight: "btn56FontWeight",
      lineHeight: "btn56LineHeight",
      "& svg": {
        width: "btn56IconSize",
        height: "btn56IconSize",
      },
    },
  },
});

export type BaseKind = "primary" | "secondary" | "borderless" | "tertiary";
export type BaseVertSize = "32" | "40" | "48" | "56" | 32 | 40 | 48 | 56;

export type Kind = BaseKind | BaseKind[];
export type VertSize = BaseVertSize | BaseVertSize[];

const highContrastStyles = {
  [WHCM]: {
    "&, &:hover, &:active": {
      ...WHCMstyles,
      backgroundColor: "WindowText",
      borderColor: "WindowText",
      color: "Window",
    },
  },
};

const primaryPressedStyle = {
  bg: "primaryBtnActiveBg",
  borderColor: "primaryBtnActiveBorder",
  color: "primaryBtnActiveText",
  "&:hover": {
    bg: "primaryBtnHoverBg",
    borderColor: "primaryBtnHoverBorder",
    color: "primaryBtnHoverText",
  },
  ...highContrastStyles,
};

const secondaryPressedStyle = {
  bg: "secondaryBtnActiveBg",
  borderColor: "secondaryBtnActiveBorder",
  color: "secondaryBtnActiveText",
  "&:hover": {
    bg: "secondaryBtnHoverBg",
    borderColor: "secondaryBtnHoverBorder",
    color: "secondaryBtnHoverText",
  },
  ...highContrastStyles,
};

const applyPressedStyle = (buttonKind: Kind) => {
  switch (buttonKind) {
    case "primary":
      return primaryPressedStyle;

    case "secondary":
      return secondaryPressedStyle;

    default:
      return {};
  }
};

export const press = (buttonKind: Kind) => (pressed?: boolean) =>
  pressed ? applyPressedStyle(buttonKind) : {};
